import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { OfficialBearInsiderShield } from "@/components/OfficialBearInsiderBadge";
import { Info } from "lucide-react";

interface OfficialBearInsiderTooltipProps {
  children: React.ReactNode;
  showShield?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export function OfficialBearInsiderTooltip({ 
  children, 
  showShield = true,
  size = 'small',
  className = ""
}: OfficialBearInsiderTooltipProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`inline-flex items-center gap-1 cursor-pointer touch-manipulation ${className}`}
            style={{ 
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none', 
              KhtmlUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              userSelect: 'none',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)'
            }}
            onTouchStart={(e) => {
              // Prevent default to avoid scrolling issues
              e.preventDefault();
            }}
          >
            {children}
            {showShield && (
              <OfficialBearInsiderShield size={size} animated={false} />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent 
          className="max-w-sm p-4 bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 shadow-lg z-50"
          side="bottom"
          align="center"
          sideOffset={8}
          onPointerDownOutside={(event) => {
            // Allow touch interactions outside to close tooltip
            event.preventDefault();
          }}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <OfficialBearInsiderShield size="small" animated={true} />
              <span className="font-bold text-amber-800">Official Bear Insider</span>
            </div>
            
            <div className="text-sm text-amber-700 space-y-2">
              <p className="font-semibold">This golden honeypot shield means:</p>
              
              <ul className="space-y-1 text-xs">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">🍯</span>
                  <span><strong>Verified Status:</strong> This user is an official SnuggleCrafters insider</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">⭐</span>
                  <span><strong>Community Recognition:</strong> Trusted member of our community</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">🚀</span>
                  <span><strong>Early Access:</strong> Gets first access to new features</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">💡</span>
                  <span><strong>Direct Influence:</strong> Helps shape the app's development</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">🎁</span>
                  <span><strong>Exclusive Content:</strong> Access to special stories and features</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">🛟</span>
                  <span><strong>Priority Support:</strong> Enhanced customer service</span>
                </li>
              </ul>
              
              <div className="pt-2 border-t border-amber-200">
                <p className="text-xs text-amber-600 italic">
                  Official Bear Insiders are carefully selected community members who help us create the most magical bedtime experiences for families worldwide.
                </p>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Simple info icon tooltip for when you just want to show the explanation
export function OfficialBearInsiderInfoTooltip({ 
  className = "",
  size = 'small'
}: { 
  className?: string;
  size?: 'small' | 'medium' | 'large';
}) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`inline-flex items-center gap-1 cursor-pointer touch-manipulation ${className}`}
            style={{ 
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none', 
              KhtmlUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              userSelect: 'none',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)'
            }}
            onTouchStart={(e) => {
              // Prevent default to avoid scrolling issues
              e.preventDefault();
            }}
          >
            <OfficialBearInsiderShield size={size} animated={true} />
            <Info className="w-3 h-3 text-amber-600" />
          </div>
        </TooltipTrigger>
        <TooltipContent 
          className="max-w-sm p-4 bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 shadow-lg z-50"
          side="bottom"
          align="center"
          sideOffset={8}
          onPointerDownOutside={(event) => {
            // Allow touch interactions outside to close tooltip
            event.preventDefault();
          }}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <OfficialBearInsiderShield size="small" animated={true} />
              <span className="font-bold text-amber-800">Official Bear Insider</span>
            </div>
            
            <div className="text-sm text-amber-700 space-y-2">
              <p className="font-semibold">This golden honeypot shield means:</p>
              
              <ul className="space-y-1 text-xs">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">🍯</span>
                  <span><strong>Verified Status:</strong> This user is an official SnuggleCrafters insider</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">⭐</span>
                  <span><strong>Community Recognition:</strong> Trusted member of our community</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">🚀</span>
                  <span><strong>Early Access:</strong> Gets first access to new features</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">💡</span>
                  <span><strong>Direct Influence:</strong> Helps shape the app's development</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">🎁</span>
                  <span><strong>Exclusive Content:</strong> Access to special stories and features</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">🛟</span>
                  <span><strong>Priority Support:</strong> Enhanced customer service</span>
                </li>
              </ul>
              
              <div className="pt-2 border-t border-amber-200">
                <p className="text-xs text-amber-600 italic">
                  Official Bear Insiders are carefully selected community members who help us create the most magical bedtime experiences for families worldwide.
                </p>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
